import { ChevronDownIcon, ChevronUpIcon, CloseIcon, InfoOutlineIcon, RepeatIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Spacer,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useBoolean, usePaginationSessionStorage, useTabStorage } from '@laxmimanogna/code-quick-components';
import React, { useContext, useState, useEffect, useMemo } from 'react';
import AppColors from '../../constants/AppColors';
import { FONT_FAMILY } from '../../constants/Theme';
import { withMultiProviders } from '../../hoc/withProvider';
import useCQMediaQuery from '../../hooks/useCQMediaQuery';
import MyChartProvider, {
  MyChartContext,
} from '../../providers/MyChartProvider';
import AllCharts from './components/tables/AllCharts';
import ArchivedCharts from './components/tables/ArchivedCharts';
import RebuttedCharts from './components/tables/RebuttedCharts';
import AuditSheetProvider, { AuditSheetContext } from '../../providers/AuditSheetProvider';
import NewChartsTable from './components/tables/NewChartsTable';
import { CHART_FILTER_INFO, MY_CHARTS_FILTER_OPTIONS, MY_CHARTS_FILTER_OPTIONS_TO_ACCOUNT_QUERY_MAP } from '../../constants/constants';
import { MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME } from './constants';
import CQContainer from '../../components/layout/CQContainer';


const MyChartsContainerV2 = props => {
  const myChartsTab = [
    {
      title: 'ALL',
      header: 'Audit Uploads',
      component: NewChartsTable,
    },
  ];
  const {
    getPaginationSession,
    setPaginationSession
  } = usePaginationSessionStorage();
  const DEFAULT_FILTER_NAME = useMemo(() => "All", []);
  const DEFAULT_FILTER_POS = useMemo(() => MY_CHARTS_FILTER_OPTIONS.indexOf(DEFAULT_FILTER_NAME), [DEFAULT_FILTER_NAME])
  const myChartContext = useContext(MyChartContext);
  const auditSheetContext = useContext(AuditSheetContext);
  const [resetFilterTrigger, rftState] = useBoolean(null);
  const [tableToggleData, setTableToggleData] = useState({
    expand:0,
    collapse:0
  })

  const { 
    handleApplyFilter, 
    filterParams, 
    getAllCharts, 
    getArchivedCharts,
    isAllChartLoading,
  } =
    myChartContext;

  const tabStorage = useTabStorage();
  const [selectedTab, setSelectedTab] = useState(tabStorage.getTab() || 0);

  useEffect(() => {tabStorage.setTab(selectedTab)}, [selectedTab]);

  const dropdownOptions = useMemo(
    () => MY_CHARTS_FILTER_OPTIONS.map(val => ({
      value: val,
      label: val,
      description: CHART_FILTER_INFO.find(info => info.name === val)?.definition,
    })),
    []
  );

  const [selectedFilter, setSelectedFilter] = useState(() => ({
    value: MY_CHARTS_FILTER_OPTIONS[tabStorage.getTab() ?? DEFAULT_FILTER_POS],
    label: MY_CHARTS_FILTER_OPTIONS[tabStorage.getTab() ?? DEFAULT_FILTER_POS]
  }));

  useEffect(() => {
    if (!selectedFilter.value) {
      return;
    }
    tabStorage.setTab(MY_CHARTS_FILTER_OPTIONS.indexOf(selectedFilter.value))
  }, [selectedFilter]);

  const marginHorizontal = useCQMediaQuery({
    lg: '12%',
    md: '6%',
    sm: '2%',
  });

  const onSearchAction = () => {
    handleApplyFilter({
      ...filterParams,
      triggerSearch: !filterParams.triggerSearch,
    });
  };

  const onFilterChange = e => {
    handleApplyFilter({
      ...filterParams,
      searchText: e.target.value
    })
  };

  const onClearSearch = () => {
    handleApplyFilter({
      ...filterParams,
      searchText: '',
      triggerSearch: !filterParams.triggerSearch,
    });
  };

  const onStatusChange = option => {
    setSelectedFilter(option);
    handleApplyFilter({
      ...filterParams,
      triggerSearch: !filterParams.triggerSearch,
      chartStatusType: option.value
    });
  };

  const onResetFilters = () => {
    setSelectedFilter(dropdownOptions[DEFAULT_FILTER_POS]);
    setPaginationSession(
      MY_CHARTS_PAGE_NAME,
      MY_CHARTS_TABLE_NAME,
      {
        ...getPaginationSession(MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME),
        page: 1,
        size: 10
      }
    );
    handleApplyFilter({
      searchText: "",
      triggerSearch: !filterParams?.triggerSearch,
      ordering: "",
      chartStatusType: DEFAULT_FILTER_NAME,
    });
    rftState.toggle();
  };

  const onOpenNotes = (obj, batchId, tabType = undefined) => {
    if (obj && Object.keys(obj).length) {
      if (obj.all) {
        delete auditSheetContext.commentFilters.sheet_name
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          page: 1,
          tabType: tabType
        })
      } else if (obj.current) {
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          sheet_name: "Sheet1",
          page: 1,
          tabType: tabType
        })
      }
    } else {
      auditSheetContext.setCommentFilters({
        ...auditSheetContext.commentFilters,
        batchId,
        sheet_name: "Sheet1",
        reverse: true,
        page: 1,
        tabType: tabType
      })
    }

  }

  return (
      <Box>
        
        <CQContainer>
          <HStack spacing={2} >
          <Heading
            my={3}
            fontSize={'24px'}
            fontFamily={FONT_FAMILY.baiJamurjee}
            fontWeight="bold"
          >
            My Charts
          </Heading>
          <Tooltip p={3} bg="whiteAlpha.900"
            color="black"
            borderRadius="md"
            border="1px solid #43dde0" label={
              <Box>
                <>
                  <p>
                    <b>My Charts</b> tab displays only the charts that you have personally uploaded. This helps you manage and review your individual contributions separately.
                  </p>
                </>
              </Box>
            } placement="top-end">
            <Icon as={InfoOutlineIcon} w={4} h={4} cursor="pointer" _hover={{ color: AppColors.secondary, transition: 'color 0.3s ease-in-out' }} color={AppColors.primary} />
          </Tooltip></HStack>

        <Box
          shadow={'md'}
          bg="white"
          p={4}
          w="100%"
          style={{ borderRadius: '12px' }}
        >
          <Flex
            align={{ base: 'stretch', lg: 'center' }}
            direction={{ base: 'column', lg: 'row' }}
            gap={3}
            pb={4}
            borderBottom="1px solid"
            borderColor="gray.200"
          >
            {/* Status filter */}
            <Menu placement="bottom-start" isLazy>
              <MenuButton
                as={Button}
                aria-label="Filter by chart status"
                size="md"
                variant="outline"
                minW="200px"
                alignSelf="flex-start"
                textAlign="left"
                fontWeight={500}
                color="gray.700"
                borderColor="gray.300"
                rightIcon={<ChevronDownIcon />}
                isLoading={isAllChartLoading}
                isDisabled={isAllChartLoading}
              >
                {selectedFilter.label}
              </MenuButton>
              <MenuList zIndex={10} maxW="360px" py={1}>
                <MenuOptionGroup
                  type="radio"
                  value={selectedFilter.value}
                  onChange={value => {
                    const option = dropdownOptions.find(o => o.value === value);
                    if (option && option.value !== selectedFilter.value) {
                      onStatusChange(option);
                    }
                  }}
                >
                  {dropdownOptions.map(option => (
                    <MenuItemOption key={option.value} value={option.value} py={2}>
                      <Text fontSize="sm" fontWeight={600} color="gray.800">
                        {option.label}
                      </Text>
                      {option.description && (
                        <Text fontSize="xs" color="gray.500" mt={0.5}>
                          {option.description}
                        </Text>
                      )}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </MenuList>
            </Menu>

            <Spacer display={{ base: 'none', lg: 'block' }} />

            <HStack spacing={2}>
              {/* Row expand / collapse */}
              <ButtonGroup size="md" isAttached variant="outline">
                <Tooltip label="Expand all rows" hasArrow openDelay={300}>
                  <IconButton
                    aria-label="Expand all rows"
                    icon={<ChevronDownIcon boxSize={5} />}
                    borderColor="gray.300"
                    color="gray.600"
                    onClick={() => setTableToggleData({
                      ...tableToggleData,
                      expand: tableToggleData.expand + 1})
                    }
                  />
                </Tooltip>
                <Tooltip label="Collapse all rows" hasArrow openDelay={300}>
                  <IconButton
                    aria-label="Collapse all rows"
                    icon={<ChevronUpIcon boxSize={5} />}
                    borderColor="gray.300"
                    color="gray.600"
                    onClick={() => setTableToggleData({
                      ...tableToggleData,
                      collapse: tableToggleData.collapse + 1})
                    }
                  />
                </Tooltip>
              </ButtonGroup>

              <Divider orientation="vertical" h="24px" borderColor="gray.300" />

              {/* Search */}
              <InputGroup size="md" w={{ base: '100%', lg: '320px' }}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  value={filterParams?.searchText ?? ''}
                  onChange={onFilterChange}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      onSearchAction();
                    }
                  }}
                  placeholder="Search charts and press Enter"
                  borderRadius="md"
                  borderColor="gray.300"
                  focusBorderColor={AppColors.secondary}
                  isDisabled={isAllChartLoading}
                />
                {filterParams?.searchText && (
                  <InputRightElement>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Clear search"
                      icon={<CloseIcon boxSize={2.5} />}
                      onClick={onClearSearch}
                      isDisabled={isAllChartLoading}
                    />
                  </InputRightElement>
                )}
              </InputGroup>

              {/* Reset */}
              <Tooltip label="Back to default view: status, search, sorting and page" hasArrow openDelay={300}>
                <Button
                  size="md"
                  variant="outline"
                  leftIcon={<RepeatIcon />}
                  fontWeight={500}
                  color="gray.700"
                  borderColor="gray.300"
                  flexShrink={0}
                  isDisabled={isAllChartLoading}
                  onClick={onResetFilters}
                >
                  Reset
                </Button>
              </Tooltip>
            </HStack>
          </Flex>

          <Box pt={3}>
            {myChartsTab.map((aa, index) => (
               (
                <aa.component 
                key={`my-charts-panel-${index}`} 
                onOpenNotes={onOpenNotes}
                PAGE_NAME={MY_CHARTS_PAGE_NAME}
                TABLE_NAME={MY_CHARTS_TABLE_NAME}
                filterResetTrigger={resetFilterTrigger}
                tableToggleData={tableToggleData}
                />
              )
            ))}
          </Box>
        </Box>
        </CQContainer>
      </Box>
  );
};

export default withMultiProviders([MyChartProvider, AuditSheetProvider], MyChartsContainerV2);